import EventEmitter from 'eventemitter3';
import { OpusDecoder } from 'opus-decoder';
import { Player } from './Player';
import { Recorder } from './Recorder';
import root from './voice-bot.proto';
import { guid } from '../../utils/objectTransforms';

interface VoiceBotRoot {
    voice: {
        Request: {
            create: (payload: unknown) => unknown;
            encode: (payload: unknown) => { finish: () => Uint8Array };
        };
        Response: {
            decode: (payload: Uint8Array) => {
                transcript?: { text: string };
                reply?: { text: string };
                audio?: { audio: Uint8Array };
            };
        };
    };
}

export const VOICE_CHAT_EVENTS = {
    STARTED: 'started',
    STOPPED: 'stopped'
};

export class VoiceChat {
    audioQueue: Uint8Array[] = [];
    endpoint: string;
    jwtToken: string;
    addMessage: (message: { isUser: boolean; text: string }) => void;
    bus: EventEmitter;
    decoder: OpusDecoder;
    options: Record<string, string>;
    callID: string;
    ws?: WebSocket;
    recorder?: Recorder;
    player?: Player;

    private getVoiceRoot() {
        return root as VoiceBotRoot;
    }

    constructor(
        endpoint: string,
        jwtToken: string,
        options: Record<string, string>,
        addMessage: (message: { isUser: boolean; text: string }) => void
    ) {
        this.endpoint = endpoint;
        this.jwtToken = jwtToken;
        this.addMessage = addMessage;
        this.bus = new EventEmitter();
        this.decoder = new OpusDecoder({ forceStereo: true });
        this.options = {
            lang: 'de',
            voice: '',
            speed: '1',
            style: '',
            vad: '',
            ...options
        };
        this.callID = guid();
    }

    setOptions(options: Record<string, string>) {
        this.options = options;
    }

    on(event: string, fn: (...args: unknown[]) => void) {
        this.bus.on(event, fn);
    }

    start() {
        const voiceRoot = this.getVoiceRoot();

        let voice = '';
        if (this.options.voice) {
            voice += `&voice=${this.options.voice}`;
            if (this.options.speed) {
                voice += `:${this.options.speed}`;
                if (this.options.style) {
                    voice += `:${this.options.style}`;
                }
            }
        }

        let vad = '';
        if (this.options.vad) {
            vad = `&vad=${this.options.vad}`;
        }

        // this.jwtToken

        this.ws = new WebSocket(`${this.endpoint}?callid=${this.callID}&lang=${this.options.lang}${voice}${vad}&codec=opus`);
        this.ws.onmessage = this.handleWebSocketMessage.bind(this);
        this.ws.onopen = () => {
            console.log('WebSocket connection established');

            const authRequest = voiceRoot.voice.Request.create({
                auth: {
                    token: this.jwtToken
                }
            });
            this.ws?.send(voiceRoot.voice.Request.encode(authRequest).finish());
        };
        this.ws.onclose = () => console.log('WebSocket connection closed');
        this.ws.onerror = (error: Event) => console.error('WebSocket error', error);

        this.recorder = new Recorder(
            (audioBlob: Uint8Array) => {
                if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                    const request = voiceRoot.voice.Request.create({
                        audio: { audio: audioBlob }
                    });
                    this.ws.send(voiceRoot.voice.Request.encode(request).finish());
                }
            },
            () => {
                this.stop();
            }
        );

        this.recorder.start();
        this.bus.emit(VOICE_CHAT_EVENTS.STARTED);
    }

    handleWebSocketMessage(event: MessageEvent) {
        if (!event.data) return;
        event.data
            .arrayBuffer()
            .then((buffer: ArrayBuffer) => {
                const parsed = this.getVoiceRoot().voice.Response.decode(new Uint8Array(buffer));

                if (parsed.transcript) {
                    this.addMessage({ isUser: true, text: parsed.transcript.text });
                } else if (parsed.reply) {
                    this.addMessage({ isUser: false, text: parsed.reply.text });
                } else if (parsed.audio) {
                    this.audioQueue.push(parsed.audio.audio);
                    this.playAudio();
                }
            })
            .catch(console.error);
    }

    stop() {
        this.stopAudio();
        this.recorder?.stop();
        this.recorder = undefined;

        if (this.ws) {
            this.ws.close();
            this.ws = undefined;
        }

        this.bus.emit(VOICE_CHAT_EVENTS.STOPPED);
    }

    playAudio() {
        if (this.audioQueue.length === 0) return;

        const opusFrame = this.audioQueue.shift() as Uint8Array;
        const { channelData, samplesDecoded, sampleRate } = this.decoder.decodeFrame(opusFrame);

        if (samplesDecoded > 0) {
            const pcmSamples = channelData[0];
            if (!this.player) {
                this.player = new Player({
                    channels: 1,
                    sampleRate,
                    flushingTime: 1000
                });
            }
            this.player.feed(pcmSamples);
        }
        this.playAudio();
    }

    stopAudio() {
        if (this.player) {
            this.player.destroy();
            this.player = undefined;
            this.decoder.reset().catch(console.error);
        }
        this.audioQueue.length = 0;
    }
}
